import { ProposalWithOwner } from "lib/frontend/cm/utils/types";
import { AnnualWorkingWeeksMap } from "lib/shared/company/types";
import {
  getInCycleSalaryIncomeFrequency,
  getInCycleEmploymentSubType,
} from "lib/shared/compensation/pay-frequency";
import { FieldProposal } from "lib/shared/fields/types";
import { SalaryIncomeFrequency } from "prisma/cm/client";

import { EMPLOYMENT_SUBTYPE_FULL_TIME } from "../employment/constants";
import { EmploymentSubType } from "../employment/types";

export const DEFAULT_SALARY_INCOME_FREQUENCY = SalaryIncomeFrequency.YEARLY;
export const DEFAULT_EMPLOYMENT_SUBTYPE = EMPLOYMENT_SUBTYPE_FULL_TIME;

const FULL_TIME_WEEKS_PER_YEAR = 52;
const PART_TIME_WEEKS_PER_YEAR = 26;
const DEFAULT_WEEKLY_WORKING_HOURS = 40;

type GetAnnualizedCashValueMultiplierParams = {
  incomeFrequency?: SalaryIncomeFrequency | null;
  employmentSubType?: EmploymentSubType | null;
  weeklyWorkingHours?: number;
  annualWorkingWeeksMap?: AnnualWorkingWeeksMap;
};

function getAnnualizedCashValueMultiplier({
  incomeFrequency,
  employmentSubType,
  weeklyWorkingHours = DEFAULT_WEEKLY_WORKING_HOURS,
  annualWorkingWeeksMap = {
    full_time: FULL_TIME_WEEKS_PER_YEAR,
    part_time: PART_TIME_WEEKS_PER_YEAR,
    intern: PART_TIME_WEEKS_PER_YEAR,
    temp: PART_TIME_WEEKS_PER_YEAR,
    seasonal: PART_TIME_WEEKS_PER_YEAR,
    individual_contractor: FULL_TIME_WEEKS_PER_YEAR,
    other: FULL_TIME_WEEKS_PER_YEAR,
  },
}: GetAnnualizedCashValueMultiplierParams): number {
  switch (incomeFrequency) {
    case SalaryIncomeFrequency.HOURLY: {
      return employmentSubType
        ? annualWorkingWeeksMap[employmentSubType as keyof AnnualWorkingWeeksMap] *
            weeklyWorkingHours
        : 1;
    }
    case SalaryIncomeFrequency.DAILY: {
      return 365;
    }
    case SalaryIncomeFrequency.WEEKLY: {
      return 52;
    }
    case SalaryIncomeFrequency.BI_WEEKLY: {
      return 26;
    }
    case SalaryIncomeFrequency.SEMI_MONTHLY: {
      return 24;
    }
    case SalaryIncomeFrequency.MONTHLY: {
      return 12;
    }
    case SalaryIncomeFrequency.YEARLY:
    case SalaryIncomeFrequency.FIXED: {
      return 1;
    }
    default: {
      // hint: this often happens when the salaryIncomeFrequency is not set due to user/ company not having a range
      console.warn("getAnnualizedCashValueMultiplier - Unknown SalaryIncomeFrequency");
      // NB: Possibly incorrect but retained for backwards-compatibility.
      return 1;
    }
  }
}

export function convertIncomeFrequency(
  value: number,
  souceIncomeFrequency: SalaryIncomeFrequency,
  targetIncomeFrequency: SalaryIncomeFrequency,
  employmentSubType: EmploymentSubType = DEFAULT_EMPLOYMENT_SUBTYPE,
  weeklyWorkingHours: number = DEFAULT_WEEKLY_WORKING_HOURS,
) {
  if (souceIncomeFrequency === targetIncomeFrequency) return value;
  return (
    (value *
      getAnnualizedCashValueMultiplier({
        incomeFrequency: souceIncomeFrequency,
        employmentSubType,
        weeklyWorkingHours,
      })) /
    getAnnualizedCashValueMultiplier({
      incomeFrequency: targetIncomeFrequency,
      employmentSubType,
      weeklyWorkingHours,
    })
  );
}

type GetAnnualizedCashValueParams = {
  value: number;
  incomeFrequency?: SalaryIncomeFrequency | null;
  // TODO: employmentSubType should be required (even if null)
  employmentSubType?: EmploymentSubType | null;
  weeklyWorkingHours?: number;
};

export function annualizeCashValue({
  value,
  incomeFrequency,
  employmentSubType,
  weeklyWorkingHours,
}: GetAnnualizedCashValueParams) {
  const multiplier = getAnnualizedCashValueMultiplier({
    incomeFrequency,
    employmentSubType,
    weeklyWorkingHours,
  });

  return value * multiplier;
}

export function deannualizeCashValue({
  value,
  incomeFrequency,
  employmentSubType,
  weeklyWorkingHours,
}: GetAnnualizedCashValueParams) {
  const multiplier = getAnnualizedCashValueMultiplier({
    incomeFrequency,
    employmentSubType,
    weeklyWorkingHours,
  });

  return value / multiplier;
}

export function annualizeProposalCashValue({
  value,
  proposalWithOwner,
}: {
  value: number;
  proposalWithOwner: FieldProposal;
}) {
  const incomeFrequency = getInCycleSalaryIncomeFrequency({ proposalWithOwner });
  const employmentSubType = getInCycleEmploymentSubType({ proposalWithOwner });

  return annualizeCashValue({
    value,
    incomeFrequency,
    employmentSubType,
  });
}

export function deannualizeProposalCashValue({
  value,
  proposalWithOwner,
}: {
  value: number;
  proposalWithOwner: ProposalWithOwner;
}) {
  const incomeFrequency = getInCycleSalaryIncomeFrequency({ proposalWithOwner });
  // COM-5421 - see note above in annualizeProposalCashValue

  const employmentSubType =
    incomeFrequency === SalaryIncomeFrequency.YEARLY
      ? "full_time"
      : (proposalWithOwner.owner.employmentSubType as EmploymentSubType);

  return deannualizeCashValue({
    value,
    incomeFrequency,
    employmentSubType,
  });
}
